import type { TriageIndex } from '../../api/useTriageIndex';
import { IMBALANCE_TOLERANCE } from '../../live/unhedged';
import { venueSignedNet, type PairVenueView } from '../../live/venuePositions';
import {
  hasTriageMarks,
  triageTooltipText,
  type TriageRowCounts
} from '../triage/triageMarkText';
import { resolveTriageRowCounts } from '../triage/triageRowCounts';

/**
 * The opener table's own slice of the triage marks (spec §6.2, §6.4, §6.6).
 * Everything shared with the Descalces page — the chip, the header notice, the
 * copy, the rule/pair join — is imported from `components/triage`; what lives
 * here is only what the opener knows and that page does not: a row whose RULE
 * is not loaded yet, the group badge, and a colour taken from the ACT column.
 */

/**
 * The `ungrouped / unresolved` bucket — an orphan on a pair with no rule (or
 * several) and a rule that no longer exists (spec §4.7). Defined beside the
 * deep link that points at it, so the picker and the tray cannot drift apart
 * on what that key is.
 */
export {
  TRIAGE_UNGROUPED_KEY,
  TRIAGE_UNGROUPED_LABEL
} from '../triage/triageDeepLink';

/** Said on the row whose counts are the PAIR's because the rule is unknown. */
export const TRIAGE_PAIR_KEYED_NOTE =
  'The rules list has not answered for this row yet, so these counts are for the whole pair.';

/** Said on a row that carries neither key — an honest "cannot tell". */
export const TRIAGE_UNKEYABLE_NOTE =
  'Triage cannot be keyed to this row — it has neither a pair id nor a rule.';

export type RowTriage =
  /** The index never loaded. No mark — the header says so instead. */
  | { kind: 'unreadable' }
  /** The index is readable but this row carries no key to look it up with. */
  | { kind: 'unknown' }
  | {
      kind: 'counts';
      counts: TriageRowCounts;
      /** Which key answered — drives the tooltip's wording. */
      keyedBy: 'rule' | 'pair';
    };

/**
 * The marks for ONE opener row.
 *
 * The rule-keyed case is the shared `resolveTriageRowCounts` verbatim. What is
 * added here is the case the Descalces page does not have: **`rulesPending`**.
 * While the rules list cannot answer for this row (cold load, or a group switch
 * still serving the previous group's rules) there is NO rule id, and reading
 * `byRuleId` with none returns 0 — asserting *"no stuck counter on this rule"*
 * out of ignorance, which is the one lie §6.7 exists to prevent.
 *
 * The fallback is not a guess: `counter_stuck` carries BOTH `ruleId` and
 * `pairId`, so `byPairId[pairId].counter` covers every rule on the pair. It is
 * a SUPERSET of the rule's own count — it can only over-report (a second rule
 * on the same pair), never claim a clean row that is not. And a pair with no
 * entry at all is a REAL absence: no card on the pair means no card on any of
 * its rules, so clean rows stay clean through the whole load.
 */
export function resolveRowTriage(input: {
  index: TriageIndex | undefined;
  ruleId?: string | null;
  pairId?: string | null;
}): RowTriage {
  const { index, ruleId, pairId } = input;
  if (!index) return { kind: 'unreadable' };
  if (ruleId) {
    const counts = resolveTriageRowCounts(index, { ruleId, pairId });
    // `index` is defined, so the shared resolver always answers.
    return { kind: 'counts', keyedBy: 'rule', counts: counts as TriageRowCounts };
  }
  if (pairId) {
    const byPair = index.byPairId?.[pairId];
    return {
      kind: 'counts',
      keyedBy: 'pair',
      counts: {
        counter: byPair?.counter ?? 0,
        orphan: byPair?.orphan ?? 0,
        latestAt: byPair?.latestAt ?? null
      }
    };
  }
  return { kind: 'unknown' };
}

/** Counts for one group's badge, or for the `null` bucket. */
export function resolveGroupTriage(
  index: TriageIndex | undefined,
  groupId: string
): TriageRowCounts | undefined {
  if (!index) return undefined;
  const entry = index.byGroupId?.[groupId];
  if (!entry) return undefined;
  return { counter: entry.counter, orphan: entry.orphan, latestAt: null };
}

/**
 * Red only when the row is unhedged RIGHT NOW; amber otherwise.
 *
 * 🚨 This is a COLOUR, never a gate. An unread `counter_stuck` on a row whose
 * NET reads hedged is exactly the case that must stay visible: either the leg
 * was hedged after the card fired (and the card still needs clearing), or venue
 * and belief disagree, or the believed position is stale.
 *
 * The source ordering mirrors `ActNetCell` so the colour can never disagree
 * with the number rendered eight columns to the right. An UNKNOWN net is amber:
 * "we cannot prove this row is naked" is not "this row is naked".
 */
export function resolveTriageColorHint(input: {
  view: PairVenueView;
  /** `|YES − NO|` from the REST positions poll. */
  restMagnitude: number;
  ruleCost?: { kalshiNet: number; polyNet: number; computedAt: number } | null;
  /** `ACT_FROM_ENGINE` — the engine net may stand in for REST. */
  engineNetAllowed: boolean;
  /** How old the engine net may be before it stops counting. */
  engineStaleMs: number;
  nowMs: number;
}): 'red' | 'amber' {
  const {
    view,
    restMagnitude,
    ruleCost,
    engineNetAllowed,
    engineStaleMs,
    nowMs
  } = input;
  if (view.kind === 'venue') {
    // A `resolving` imbalance is a settled leg awaiting redemption, not an
    // open naked leg — the same call `venueSortMagnitude` makes.
    if (view.resolving) return 'amber';
    return Math.abs(venueSignedNet(view.venue)) > IMBALANCE_TOLERANCE
      ? 'red'
      : 'amber';
  }
  if (view.kind === 'unavailable') return 'amber';
  if (
    engineNetAllowed &&
    ruleCost &&
    nowMs - ruleCost.computedAt <= engineStaleMs
  ) {
    return Math.abs(ruleCost.polyNet - ruleCost.kalshiNet) > IMBALANCE_TOLERANCE
      ? 'red'
      : 'amber';
  }
  return restMagnitude > IMBALANCE_TOLERANCE ? 'red' : 'amber';
}

/**
 * The opener row's tooltip — the shared tray copy, plus the two things only
 * this surface can say.
 *
 * EMPTY on a clean row: the table's hover ban means the `<Tooltip>` must have
 * nothing to show where there is nothing to report.
 */
export function buildOpenerTriageTooltip(row: RowTriage): string {
  if (row.kind === 'unreadable') return '';
  if (row.kind === 'unknown') return TRIAGE_UNKEYABLE_NOTE;
  if (!hasTriageMarks(row.counts)) return '';
  const text = triageTooltipText(row.counts, {
    unattributedNote: row.counts.orphan > 0
  });
  return row.keyedBy === 'pair' ? `${TRIAGE_PAIR_KEYED_NOTE} ${text}` : text;
}
